import { FC, useContext, useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router";
import { AxiosError } from "axios";

import { useGetDrugPricesList, useGetUserLocation } from "@/api-hooks";
import { convertCapsToPascal } from "@/utils/convertCapsToPascal";
import {
  useGetURLQuery,
  useSetQuery,
  useWindowDimension,
} from "capitalrx-components-2.0";
import { useAlert } from "capitalrx-components-2.0/lib/general";

import ErrorSection from "@/components/ErrorSection";
import FindBestPriceList from "@/components/FindBestPriceList";
import FindBestPriceMap from "@/components/FindBestPriceMap";
import FindBestPriceResultsLayout from "@/layout/FindBestPriceResults";
import PageLayout from "@/layout/Page";
import { WhiteLabelLayout, WhiteLabelResultsLayout } from "@/layout/WhiteLabel";

import { ThemeSelectorContext } from "@/context/index";
import { PharmacyResults } from "@/api/interfaces/PharmacyResults";
import { DrugPrices, MemberSelectedDrugConfiguration, Coordinates, QueryParams, GetDrugApiError } from "@/api/interfaces";

import styles from "./PriceList.module.scss";

const PriceList: FC = () => {
  const { client } = useContext(ThemeSelectorContext);
  const [toggleMapViewMobile, setToggleMapViewMobile] = useState<boolean>(false);
  const queryParams = useGetURLQuery<QueryParams>(null);
  const { sort_by, dosage, dosage_form, drug_id, quantity, zipcode, drug_name, group_number } = queryParams;
  let allUrlDataPresent = false;
  if (dosage && dosage_form && drug_id && quantity) allUrlDataPresent = true;

  const userLocationResults = useGetUserLocation({ search_address: zipcode });
  const userLocation = userLocationResults.data;
  const [coordinates, setCoordinates] = useState<Coordinates>({ lat: null, lng: null });
  const [priceRejected, setPriceRejected] = useState(false);

  const [_, setQuery] = useSetQuery();
  const navigate = useNavigate();

  const showAlert = useAlert();
  const ref = useRef<HTMLDivElement>();

  const toggleMapWindow = useWindowDimension(992);

  let drugToSearch: MemberSelectedDrugConfiguration;

  if (userLocation?.longitude === "" || userLocation?.latitude === "") {
    showAlert({
      type: "error",
      message: `The zipcode you entered - ${zipcode} - is not valid. Please return to search page and try again.`,
      style: "banner",
    });
  }

  if (!allUrlDataPresent) {
    showAlert({
      type: "error",
      message: "No drug information available for search. Please return to search page and try again.",
      style: "banner",
    });
  } else if (userLocation?.latitude && coordinates?.lat) {
    drugToSearch = {
      dosage: dosage,
      dosage_form: dosage_form,
      drug_id: parseInt(drug_id),
      quantity: quantity.match(/\d+/)[0],
      location: {
        latitude: coordinates?.lat.toString(),
        longitude: coordinates?.lng.toString(),
      },
    };

    if (group_number !== undefined) {
      drugToSearch.group_number = `CRA${group_number}`;
    }

    if (coordinates.bounding_box) {
      drugToSearch.bounding_box = coordinates.bounding_box;
    }
  }

  const { isLoading, error, data, isFetching, refetch, isError } = useGetDrugPricesList(drugToSearch, {
    enabled: coordinates.lat !== null && userLocation !== undefined,
  });

  const errorHandler = (error: GetDrugApiError | AxiosError<{ message?: string }>): void => {
    const message = "data" in error ? error.data.message : error.response?.data?.message || error.message;

    showAlert({ type: "error", message, style: "banner" });
  };

  if (error) errorHandler(error);

  if (!isLoading && JSON.stringify(data) === "{}")
    errorHandler({
      status: 500,
      data: {
        message: "Error! No results found. Please go back and try another search.",
        endpoint_url: "/drug/prices/list",
        location: {},
        input_body: drugToSearch,
      },
    });

  const formatData = (data: DrugPrices[], sort = false): PharmacyResults[] => {
    const pharmacyResultsList = data.map((item) => {
      const { claim, pharmacy } = item;
      const distance = Number(pharmacy?.distance?.toFixed(2));

      // If the price is rejected, we use Number.MAX_SAFE_INTEGER to asign a huge number so that it's sent to the bottom of the sorted list.
      const sort_price = Number(claim.response.response_pricing?.patient_pay_amount) || Number.MAX_SAFE_INTEGER;
      const isMaxUsualAndCustomaryCharge = claim.response.response_pricing?.is_max_usual_and_customary_charge;

      let sort_criteria = sort_price;
      if (sort_by === "distance") {
        sort_criteria = distance;
      }

      const ndc = claim.request.claim?.ndc;

      return {
        pharmacy: {
          name: pharmacy?.name,
          address_line_1: pharmacy ? convertCapsToPascal(pharmacy.address_line_1) : "",
          address_line_2: pharmacy ? convertCapsToPascal(pharmacy.address_line_2) : "",
          city: pharmacy ? convertCapsToPascal(pharmacy.city) : "",
          state: pharmacy?.state,
          zip: pharmacy?.zip,
          phone: pharmacy?.phone,
          hours: pharmacy?.hours,
          is_24_hours: pharmacy?.is_24_hours,
          languages: pharmacy?.languages,
          latitude: pharmacy?.latitude,
          longitude: pharmacy?.longitude,
          provider_id: pharmacy?.provider_id,
        },
        sort_criteria: sort_criteria,
        sort_price: sort_price,
        price: Number(claim.response.response_pricing?.patient_pay_amount),
        distance: distance,
        drug: {
          name: drug_name,
          genericAlternativeDrugName: ndc ? claim.formulary[ndc]?.brand_name : null,
          dosage: dosage,
          dosage_form: dosage_form,
          drug_id: Number(drug_id),
        },
        is_max_usual_and_customary_charge: isMaxUsualAndCustomaryCharge,
      };
    });

    if (sort) {
      return pharmacyResultsList.sort((a: PharmacyResults, b: PharmacyResults) => a.sort_criteria - b.sort_criteria);
    }
    return pharmacyResultsList;
  };

  const pharmacyElementsList = useMemo(() => {
    if (!isLoading && data?.prices?.length >= 1 && allUrlDataPresent) {
      return formatData(data.prices, true);
    }
    return [];
  }, [isLoading, data?.prices, allUrlDataPresent, sort_by, drug_name, dosage, dosage_form, drug_id]);

  const genericAlternative = useMemo(() => {
    if (!isLoading && data?.generic_prices?.length > 0) {
      return formatData(data.generic_prices);
    }
    return [];
  }, [isLoading, data?.generic_prices, sort_by, drug_name, dosage, dosage_form, drug_id]);

  useEffect(() => {
    if (userLocationResults.isSuccess && userLocation.latitude && coordinates.lat === null) {
      setCoordinates({
        lat: Number(userLocation?.latitude),
        lng: Number(userLocation?.longitude),
      });
    }

    if (!isError) {
      for (let i = 0; i < data?.prices.length; i++) {
        if (data?.prices[i]?.claim?.transmission?.claim_status === "P") {
          setPriceRejected(false);
          break;
        }
        setPriceRejected(true);
      }
    }

    ref.current.scrollIntoView();
  }, [
    coordinates,
    userLocationResults.data,
    userLocation?.latitude,
    userLocation?.longitude,
    userLocationResults.isSuccess,
    isError,
    data,
  ]);

  const onToggleMapViewButtonClick = (): void => {
    setToggleMapViewMobile(!toggleMapViewMobile);
  };

  const updateCoords = (coords: Coordinates) => {
    setCoordinates(coords);
  };

  const defaultMapCoordinates = {
    lat: coordinates.lat,
    lng: coordinates.lng,
  };

  const MapComponent = () => {
    if (toggleMapWindow) {
      if (toggleMapViewMobile) {
        return (
          <FindBestPriceMap
            pharmacyResults={pharmacyElementsList}
            defaultMapCoordinates={defaultMapCoordinates}
            isLoading={isLoading}
            isFetching={isFetching}
            mapView={toggleMapViewMobile}
            toggleMapView={onToggleMapViewButtonClick}
            updateCoordinates={updateCoords}
            allowRedoSearch={true}
          />
        );
      } else {
        return null;
      }
    } else {
      return (
        <FindBestPriceMap
          pharmacyResults={pharmacyElementsList}
          defaultMapCoordinates={defaultMapCoordinates}
          isLoading={isLoading}
          isFetching={isFetching}
          mapView={toggleMapViewMobile}
          toggleMapView={onToggleMapViewButtonClick}
          updateCoordinates={updateCoords}
          allowRedoSearch={true}
        />
      );
    }
  };

  const switchToGenericHandler = () => {
    navigate({
      search: setQuery({
        drug_id: `${data?.generic_alternative?.id}`,
        drug_name: `${data?.generic_alternative?.drug_name}`,
        dosage: `${dosage}`,
        dosage_form: `${dosage_form}`,
        quantity: `${quantity}`,
      }),
    });

    refetch;
  };
  let showError = false;
  if (userLocationResults.error || error) {
    showError = true;
  }

  if (!priceRejected) {
    return (
      <>
        <div ref={ref}></div>
        <header className={styles.HideFromDisplay}>Find Best Price Pharmacy List Results</header>
        {client && (
          <WhiteLabelResultsLayout showError={showError}>
            <FindBestPriceList
              pharmacyResults={pharmacyElementsList}
              isLoading={isLoading || isFetching}
              genericAlternative={genericAlternative}
              switchToGeneric={switchToGenericHandler}
              mapView={toggleMapViewMobile}
              toggleMapView={onToggleMapViewButtonClick}
            />
            <MapComponent />
          </WhiteLabelResultsLayout>
        )}
        {!client && (
          <FindBestPriceResultsLayout showError={showError}>
            <FindBestPriceList
              pharmacyResults={pharmacyElementsList}
              isLoading={isLoading || isFetching}
              genericAlternative={genericAlternative}
              switchToGeneric={switchToGenericHandler}
              mapView={toggleMapViewMobile}
              toggleMapView={onToggleMapViewButtonClick}
            />
            <MapComponent />
          </FindBestPriceResultsLayout>
        )}
      </>
    );
  } else {
    return (
      <>
        {client && (
          <WhiteLabelLayout>
            <ErrorSection type="reject" />
          </WhiteLabelLayout>
        )}
        {!client && (
          <PageLayout>
            <ErrorSection type="reject" />
          </PageLayout>
        )}
      </>
    );
  }
};

export default PriceList;
